import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge, Button, Card, KV, Loader, PageHead, dateTimeStr, inr, num, titleCase,
} from '../components/ui';
import { orderApi } from '../api';
import { API_BASE } from '../api/client';
import { useToast } from '../context/ToastContext';

const STEPS = ['placed', 'accepted', 'packed', 'dispatched', 'in_transit', 'delivered'];

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [order, setOrder] = useState(null);
  const [track, setTrack] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([orderApi.get(id), orderApi.track(id)])
      .then(([o, t]) => {
        setOrder(o.data);
        setTrack(t.data);
      })
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) return <Loader label="Loading order…" />;
  if (!order) return null;

  const closed = ['cancelled', 'rejected'].includes(order.status);
  const address = order.deliveryAddress || {};

  return (
    <>
      <PageHead
        title={order.orderNumber}
        subtitle={`Placed ${dateTimeStr(order.createdAt)} · ${order.type.replace('_', ' ')} order`}
        actions={
          <>
            <Button variant="ghost" onClick={() => navigate('/admin/orders')}>← All orders</Button>
            <Button
              variant="soft"
              onClick={() => window.open(`${API_BASE}/orders/${order._id}/invoice`, '_blank')}
            >
              🧾 View invoice
            </Button>
          </>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 14 }}>
        <div className="col" style={{ gap: 14 }}>
          {/* ------------------------------------------------- progress */}
          <Card title="Fulfilment progress">
            {closed ? (
              <div className="row" style={{ gap: 10 }}>
                <Badge status={order.status} />
                <span className="text-muted">
                  {order.cancelReason || order.rejectionReason || 'This order was closed.'}
                </span>
              </div>
            ) : (
              <div className="row wrap" style={{ gap: 0, alignItems: 'stretch' }}>
                {STEPS.map((step, index) => {
                  const event = (track?.timeline || []).find(t => t.status === step);
                  const done = event?.done;
                  const current = event?.current;
                  return (
                    <div key={step} style={{ flex: 1, minWidth: 110, textAlign: 'center', position: 'relative' }}>
                      {index > 0 && (
                        <div
                          style={{
                            position: 'absolute', left: '-50%', top: 13, width: '100%', height: 2,
                            background: done ? 'var(--green-400)' : 'var(--line)',
                          }}
                        />
                      )}
                      <div
                        style={{
                          width: 28, height: 28, borderRadius: '50%', margin: '0 auto 8px',
                          display: 'grid', placeItems: 'center', position: 'relative', zIndex: 1,
                          background: done ? 'var(--green-500)' : 'var(--surface)',
                          border: `2px solid ${done ? 'var(--green-500)' : 'var(--line)'}`,
                          color: done ? '#fff' : 'var(--muted)',
                          fontSize: 12, fontWeight: 700,
                          animation: current ? 'pulseRing 1.9s infinite' : undefined,
                        }}
                      >
                        {done ? '✓' : index + 1}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: done ? 700 : 500, color: done ? 'var(--ink)' : 'var(--muted)' }}>
                        {titleCase(step)}
                      </div>
                      {event?.at && <div style={{ fontSize: 10.5, color: 'var(--muted)' }}>{dateTimeStr(event.at)}</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* ---------------------------------------------------- items */}
          <Card title={`Items (${order.items.length})`} bodyClass="">
            <div className="table-wrap" style={{ border: 'none', boxShadow: 'none', borderRadius: 0 }}>
              <table className="data">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th style={{ textAlign: 'center' }}>Qty</th>
                    <th style={{ textAlign: 'right' }}>Rate</th>
                    <th style={{ textAlign: 'right' }}>Taxable</th>
                    <th style={{ textAlign: 'center' }}>GST</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map(item => (
                    <tr key={item._id}>
                      <td>
                        <div className="cell-strong">{item.name}</div>
                        <div className="cell-muted">
                          {[item.sku, item.variantLabel, item.isContractRate && 'contract rate'].filter(Boolean).join(' · ')}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>{num(item.quantity)} {item.unit}</td>
                      <td style={{ textAlign: 'right' }}>{inr(item.unitPrice)}</td>
                      <td style={{ textAlign: 'right' }}>{inr(item.taxableAmount)}</td>
                      <td style={{ textAlign: 'center' }}>{item.gstRate}%</td>
                      <td style={{ textAlign: 'right' }} className="cell-strong">{inr(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ padding: 18, borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{ width: 290 }}>
                <div className="row-between text-sm" style={{ padding: '4px 0' }}><span className="text-muted">Taxable value</span><span>{inr(order.subtotal)}</span></div>
                {order.igst > 0 ? (
                  <div className="row-between text-sm" style={{ padding: '4px 0' }}><span className="text-muted">IGST</span><span>{inr(order.igst)}</span></div>
                ) : (
                  <>
                    <div className="row-between text-sm" style={{ padding: '4px 0' }}><span className="text-muted">CGST</span><span>{inr(order.cgst)}</span></div>
                    <div className="row-between text-sm" style={{ padding: '4px 0' }}><span className="text-muted">SGST</span><span>{inr(order.sgst)}</span></div>
                  </>
                )}
                {order.deliveryCharges > 0 && (
                  <div className="row-between text-sm" style={{ padding: '4px 0' }}><span className="text-muted">Delivery</span><span>{inr(order.deliveryCharges)}</span></div>
                )}
                <div className="divider" style={{ margin: '8px 0' }} />
                <div className="row-between" style={{ fontSize: 17, fontWeight: 800, color: 'var(--green-700)' }}>
                  <span>Grand total</span><span>{inr(order.grandTotal)}</span>
                </div>
                <div className="row-between text-sm mt-1">
                  <span className="text-muted">Platform commission ({order.commissionPercent}%)</span>
                  <span>{inr(order.commissionAmount)}</span>
                </div>
              </div>
            </div>
          </Card>

          {/* -------------------------------------------------- history */}
          <Card title="Status history">
            <div className="timeline">
              {[...order.statusHistory].reverse().map((event, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={index} className={`tl-item ${index === 0 ? 'current' : 'done'}`}>
                  <div className="tl-title">{titleCase(event.status)}</div>
                  <div className="tl-time">{dateTimeStr(event.at)}</div>
                  {event.note && <div className="text-sm text-muted">{event.note}</div>}
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* ---------------------------------------------------- sidebar */}
        <div className="col" style={{ gap: 14 }}>
          <Card title="Status">
            <div className="col" style={{ gap: 10 }}>
              <div className="row-between"><span className="text-muted">Order</span><Badge status={order.status} /></div>
              <div className="row-between"><span className="text-muted">Payment</span><Badge status={order.payment.status} /></div>
              <div className="row-between"><span className="text-muted">Mode</span><span>{titleCase(order.payment.mode)}</span></div>
              <div className="divider" style={{ margin: 0 }} />
              <div className="row-between"><span className="text-muted">Paid</span><strong>{inr(order.payment.paidAmount)}</strong></div>
              <div className="row-between"><span className="text-muted">Outstanding</span><strong style={{ color: order.payment.dueAmount > 0 ? 'var(--danger)' : 'var(--success)' }}>{inr(order.payment.dueAmount)}</strong></div>
            </div>
          </Card>

          <Card title="Parties">
            <KV
              items={[
                { label: 'Buyer', value: `${order.buyer?.name} (${order.buyer?.code})` },
                { label: 'Buyer GSTIN', value: order.buyer?.gstNumber || '—' },
                { label: 'Placed by', value: order.buyerUser ? `${order.buyerUser.name} · ${order.buyerUser.mobile}` : '—' },
                { label: 'Seller', value: `${order.seller?.name} (${order.seller?.code})` },
                { label: 'Seller GSTIN', value: order.seller?.gstNumber || '—' },
              ]}
            />
          </Card>

          <Card title="Delivery">
            <div className="text-sm">
              <div className="strong">{address.contactName || order.buyer?.name}</div>
              <div className="text-muted">
                {[address.line1, address.line2, address.city, address.state, address.pincode].filter(Boolean).join(', ')}
              </div>
              {address.contactMobile && <div className="text-muted mt-1">📞 {address.contactMobile}</div>}
            </div>
            <div className="divider" />
            <KV
              items={[
                { label: 'Expected', value: dateTimeStr(order.expectedDeliveryDate) },
                order.deliveredAt && { label: 'Delivered', value: dateTimeStr(order.deliveredAt) },
                order.warehouse && { label: 'Shipped from', value: order.warehouse.name },
                order.shipment && { label: 'Shipment', value: order.shipment.shipmentNumber },
              ]}
            />
          </Card>

          {order.invoice?.number && (
            <Card title="Invoice">
              <KV
                items={[
                  { label: 'Number', value: <span className="mono-chip">{order.invoice.number}</span> },
                  { label: 'Generated', value: dateTimeStr(order.invoice.generatedAt) },
                ]}
              />
              <Button
                block
                variant="soft"
                className="mt-2"
                onClick={() => window.open(`${API_BASE}/orders/${order._id}/invoice`, '_blank')}
              >
                Open printable GST invoice
              </Button>
            </Card>
          )}

          {(order.buyerNotes || order.sellerNotes) && (
            <Card title="Notes">
              {order.buyerNotes && <p className="text-sm"><strong>Buyer:</strong> {order.buyerNotes}</p>}
              {order.sellerNotes && <p className="text-sm mt-1"><strong>Seller:</strong> {order.sellerNotes}</p>}
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

export default OrderDetail;
